import React, { useState, useEffect } from 'react';
import './EmployeeTerminationReport.css';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { selectStyles } from '../../../assets/Styles/selectStyle';
import { capitalizeFirstLetter } from '../../../utils/validations';
import { getStates } from '../../../services/monthlyAttendanceService';
import { getDistrict } from '../../../services/monthlyAttendanceService';
import { terminationReportEmployee } from '../../../services/resignationService';


const EmployeeTerminationReport = () => {
    const [isFocused, setIsFocused] = useState({
        state: false,
        district: false,
        fromDate: false,
        toDate: false,
    });

    const [state, setState] = useState(3);
    const [stateOptions, setStateOptions] = useState([]);
    const [district, setDistrict] = useState(null);
    const [districtOptions, setDistrictOptions] = useState([]);
    const [errors, setErrors] = useState({});
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchStates = async () => {
            try {
                const res = await getStates();
                setStateOptions(res.map(item => ({
                    value: item.id,
                    label: capitalizeFirstLetter(item.stateName)
                })));
            } catch (err) {
                setErrors(prev => ({ ...prev, general: "Failed to load states" }));
            }
        };
        fetchStates();
    }, []);

    useEffect(() => {
        if (state) {
            const fetchDistricts = async () => {
                try {
                    const data = await getDistrict(state);
                    setDistrictOptions(
                        data.map(item => ({
                            value: item.districtId,
                            label: item.districtName,
                        }))
                    );
                } catch (error) {
                    console.error('Error fetching districts:', error);
                    setErrors(prev => ({ ...prev, general: 'Failed to load districts' }));
                }
            };
            fetchDistricts();
        } else {
            setDistrictOptions([]);
            setDistrict(null);
        }
    }, [state]);

    const handleInputChange = (selectedOption, field) => {
        const value = selectedOption ? selectedOption.value : null;

        switch (field) {
            case 'state':
                setState(value);
                setDistrict(null);
                break;
            case 'district':
                setDistrict(value);
                break;
            default:
                break;
        }
        setErrors(prev => ({ ...prev, [field]: null }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!state) newErrors.state = "State is required";
       // if (!district) newErrors.district = "District is required";
        if (!fromDate) newErrors.fromDate = "From date is required";
        if (!toDate) newErrors.toDate = "To date is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleClear = () => {
        //setState(null);
        setDistrict(null);
        setFromDate(null);
        setToDate(null);
        setReportData([]);
        setErrors({});
    };

    const handleGetReport = async () => {
        if (!validateForm()) return;

        setLoading(true);
        try {
            const response = await terminationReportEmployee(state, district, fromDate, toDate);
            setReportData(response || []);
            if (response && response.length === 0) {
                setErrors(prev => ({ ...prev, general: "No data found for selected criteria" }));
            } else {
                setErrors(prev => ({ ...prev, general: null }));
            }
        } catch (err) {
            console.error("Error fetching report:", err);
            setErrors(prev => ({
                ...prev,
                general: err.response?.data?.message || err.message || "Failed to fetch termination report"
            }));
            setReportData([]);
        } finally {
            setLoading(false);
        }
    };

    const exportToExcel = () => {
        if (!reportData || reportData.length === 0) {
            alert("No data to export");
            return;
        }

        // Prepare data for Excel
        const excelData = reportData.map(item => ({
            'S.No': item.id,
            'Employee Name': item.employeeName,
            'State': item.state,
            'District': item.district,
            'Designation': item.designation,
            'Email': item.email,
            'Contact': item.contact,
            'Joining Date': item.joiningDate,
            'Request Date': item.requestDate,
            'Requested Termination Date': item.requestedTerminationDate,
            'Reason': item.reason,
            'PM Reason': item.pmReason || 'N/A',
            'Status': item.status,
            'Request By': item.requestBy,
            'Approved By': item.approvedBy || 'Pending'
        }));

        // Create worksheet
        const ws = XLSX.utils.json_to_sheet(excelData);

        // Set column widths
        const colWidths = [
            { wch: 6 },  // S.No
            { wch: 20 }, // Employee Name
            { wch: 15 }, // State
            { wch: 15 }, // District
            { wch: 20 }, // Designation
            { wch: 25 }, // Email
            { wch: 12 }, // Contact
            { wch: 12 }, // Joining Date
            { wch: 12 }, // Request Date
            { wch: 22 }, // Requested Termination Date
            { wch: 30 }, // Reason
            { wch: 20 }, // PM Reason
            { wch: 20 }, // Status
            { wch: 15 }, // Request By
            { wch: 20 }  // Approved By
        ];
        ws['!cols'] = colWidths;

        // Create workbook
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Termination Report');

        // Generate filename with current date
        const fileName = `Termination_Report_${format(new Date(), 'yyyy-MM-dd_HH-mm-ss')}.xlsx`;

        // Save file
        XLSX.writeFile(wb, fileName);
    };

    // Status badge color mapping
    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'LEVEL_2_PM_PMO':
                return 'status-pm-pmo';
            case 'LEVEL_1_APM':
                return 'status-apm';
            case 'APPROVED':
                return 'status-approved';
            case 'REJECTED':
                return 'status-rejected';
            default:
                return 'status-pending';
        }
    };

    const getStatusDisplayName = (status) => {
        switch (status) {
            case 'LEVEL_2_PM_PMO':
                return 'Level 2 - PM/PMO';
            case 'LEVEL_1_APM':
                return 'Level 1 - APM';
            case 'APPROVED':
                return 'Approved';
            case 'REJECTED':
                return 'Rejected';
            default:
                return status || 'Pending';
        }
    };

    return (
        <div className="employee-termination-report-maincontainer">
            <div className="employee-termination-report-box">
                <h2 className="employee-termination-report-heading">Employee Termination Report</h2>
                {errors.general && <div className="error-message">{errors.general}</div>}

                <div className="employee-termination-report-structure-heading">
                    <div className="employee-termination-report-maincontainer-1">
                        <div className="employee-termination-report-container-part">
                            <div className={`employee-termination-report-floating-label-container-2 ${isFocused.state || state ? 'float-label' : ''}`}>
                                <Select
                                    name="state"
                                    options={stateOptions}
                                    value={stateOptions.find(opt => opt.value === state) || null}
                                    onChange={option => handleInputChange(option, 'state')}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, state: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, state: false }))}
                                    placeholder=" "
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}
                                />
                                <label><span className="required">*</span>State</label>
                                {errors.state && <div className="error-message">{errors.state}</div>}
                            </div>
                        </div>
                        <div className="employee-termination-report-container-part">
                            <div className={`employee-termination-report-floating-label-container-2 ${isFocused.district || district ? 'float-label' : ''}`}>
                                <Select
                                    name="district"
                                    options={districtOptions}
                                    value={districtOptions.find(opt => opt.value === district) || null}
                                    onChange={option => handleInputChange(option, 'district')}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, district: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, district: false }))}
                                    placeholder=" "
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}
                                />
                                <label>District</label>
                                {errors.district && <div className="error-message">{errors.district}</div>}
                            </div>
                        </div>
                    </div>

                    <div className="employee-termination-report-maincontainer-1">
                        <div className="employee-termination-report-container-part">
                            <div className={`employee-termination-report-floating-label-container-2 ${isFocused.fromDate || fromDate ? "float-label" : ""}`}>
                                <DatePicker
                                    selected={fromDate}
                                    onChange={(date) => setFromDate(date)}
                                    dateFormat="dd-MM-yyyy"
                                    placeholderText=""
                                    className="did-floating-input"
                                    maxDate={toDate || new Date()}
                                />
                                <label><span className="required">*</span>From Date</label>
                                {errors.fromDate && <div className="error-message">{errors.fromDate}</div>}
                            </div>
                        </div>
                        <div className="employee-termination-report-container-part">
                            <div className={`employee-termination-report-floating-label-container-2 ${isFocused.toDate || toDate ? "float-label" : ""}`}>
                                <DatePicker
                                    selected={toDate}
                                    onChange={(date) => setToDate(date)}
                                    dateFormat="dd-MM-yyyy"
                                    placeholderText=""
                                    className="did-floating-input"
                                    minDate={fromDate}
                                    maxDate={new Date()}
                                />
                                <label><span className="required">*</span>To Date</label>
                                {errors.toDate && <div className="error-message">{errors.toDate}</div>}
                            </div>
                        </div>
                    </div>

                    <div className="employee-termination-report-btn-div">
                        <button type="button" className="employee-termination-report-cancel" onClick={handleClear}>
                            Clear
                        </button>
                        <button type="button" className="employee-termination-report-btn" onClick={handleGetReport} disabled={loading}>
                            {loading ? 'Loading...' : 'Get Report'}
                        </button>
                        {/* {reportData.length > 0 && (
                            <button type="button" className="employee-termination-report-excel" onClick={exportToExcel}>
                                Export to Excel
                            </button>
                        )} */}
                    </div>
                </div>
            </div>

            {/* Table Display */}
            {reportData.length > 0 && (
                <div className="employee-termination-report-table-container">
                    <div className="employee-termination-report-table-wrapper">
                        <table className="employee-termination-report-table">
                            <thead>
                                <tr>
                                    <th>S.No</th>
                                    <th>State</th>
                                    <th>District</th>
                                    <th>Employee Name</th>
                                    <th>Designation</th>
                                    <th>Email</th>
                                    <th>Mobile No</th>
                                    <th>Joining Date</th>
                                    <th>Request Date</th>
                                    <th>Termination Date</th>
                                    <th>Reason</th>
                                    <th>PM Reason</th>
                                    <th>Status</th>
                                    <th>Request By</th>
                                    <th>Approved By</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.map((item, index) => (
                                    <tr key={item.id}>
                                        <td>{index + 1}</td>
                                        <td>{item.state}</td>
                                        <td>{item.district}</td>
                                        <td>{item.employeeName}</td>
                                        <td>{item.designation}</td>
                                        <td>{item.email}</td>
                                        <td>{item.contact}</td>
                                        <td>{format(item.joiningDate, 'dd-MM-yyyy')}</td>
                                        <td>{format(item.requestDate,'dd-MM-yyyy')}</td>
                                        <td>{format(item.requestedTerminationDate, 'dd-MM-yyyy')}</td>
                                        <td >{item.reason}</td>
                                        <td>{item.pmReason || '-'}</td>
                                        <td>
                                            <span className={`status-badge ${getStatusBadgeClass(item.status)}`}>
                                                {getStatusDisplayName(item.status)}
                                            </span>
                                        </td>
                                        <td>{item.requestBy}</td>
                                        <td>{item.approvedBy || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmployeeTerminationReport;